<template>
  <transition name="tw-toast">
    <div v-if="visible" :class="['tw-toast', `tw-toast--${type}`]" role="alert" aria-live="polite">
      <div class="tw-toast__icon">
        <i :class="iconClass"></i>
      </div>
      <div class="tw-toast__body">
        <div v-if="title" class="tw-toast__title">{{ title }}</div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="tw-toast__message" v-html="message"></div>
      </div>
      <button v-if="action" class="tw-toast__action" type="button" data-testid="btn-toast-action" @click="runAction">
        {{ action.label }}
      </button>
      <button
        class="tw-toast__close"
        type="button"
        aria-label="Close notification"
        data-testid="btn-toast-close"
        @click="dismiss"
      >
        <i class="fal fa-times"></i>
      </button>
    </div>
  </transition>
</template>

<script>
const ICON_MAP = {
  success: 'fal fa-check-circle',
  info: 'fal fa-info-circle',
  warning: 'fal fa-exclamation-triangle',
  danger: 'fal fa-times-circle',
};

export default {
  name: 'TwToast',

  props: {
    id: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: 'info',
      validator: (v) => ['success', 'info', 'warning', 'danger'].includes(v),
    },
    title: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      required: true,
    },
    action: {
      type: Object,
      default: null,
    },
    /** Auto-dismiss after this many ms. Pass 0 to disable. */
    duration: {
      type: Number,
      default: 5000,
    },
  },

  data() {
    return {
      visible: false,
      timerId: null,
    };
  },

  computed: {
    iconClass() {
      return ICON_MAP[this.type] || ICON_MAP.info;
    },
  },

  mounted() {
    // Start hidden so Vue sees the false → true transition and plays the enter animation
    this.$nextTick(() => {
      this.visible = true;

      if (this.duration > 0) {
        this.timerId = setTimeout(() => {
          this.dismiss();
        }, this.duration);
      }
    });
  },

  beforeDestroy() {
    if (this.timerId) {
      clearTimeout(this.timerId);
    }
  },

  methods: {
    runAction() {
      this.action.handler();
      this.dismiss();
    },
    dismiss() {
      this.visible = false;
      // Allow exit animation to complete before removing from DOM
      setTimeout(() => {
        this.$emit('dismiss', this.id);
      }, 200);
    },
  },
};
</script>

<style>
/* Container — fixed lower-left, stacks newest at bottom */
.tw-toast-container {
  position: fixed;
  bottom: 1.5rem;
  left: 1.5rem;
  /* Top of the ladder — a toast must be readable over anything. See
     design-system/styles/layers.css. */
  z-index: var(--tw-layer-toast);
  display: flex;
  flex-direction: column-reverse;
  gap: 0.5rem;
  pointer-events: none;
}

.tw-toast-container > * {
  pointer-events: auto;
}

/* Toast card */
.tw-toast {
  min-width: 280px;
  max-width: 400px;
  width: max-content;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-radius: 8px;
  border: 1px solid transparent;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  font-size: 0.875rem;
  line-height: 1.4;
}

.tw-toast__action {
  flex: none;
  align-self: center;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}

/* Variants */
.tw-toast--success {
  background: #d1e7dd;
  color: #0f5132;
  border-color: #a3cfbb;
}

.tw-toast--info {
  background: #cfe2ff;
  color: #084298;
  border-color: #9ec5fe;
}

.tw-toast--warning {
  background: #fff3cd;
  color: #664d03;
  border-color: #ffe69c;
}

.tw-toast--danger {
  background: #f8d7da;
  color: #842029;
  border-color: #f5c2c7;
}

/* Icon */
.tw-toast__icon {
  flex-shrink: 0;
  font-size: 1.1rem;
  margin-top: 1px;
}

/* Body */
.tw-toast__body {
  flex: 1;
  min-width: 0;
}

.tw-toast__title {
  font-weight: 600;
  margin-bottom: 2px;
}

.tw-toast__message {
  opacity: 0.9;
}

/* Close button */
.tw-toast__close {
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0;
  margin-left: 0.25rem;
  cursor: pointer;
  opacity: 0.6;
  font-size: 0.875rem;
  color: inherit;
  line-height: 1;
  margin-top: 1px;
}

.tw-toast__close:hover {
  opacity: 1;
}

/* Transitions */
.tw-toast-enter-active {
  transition:
    opacity 300ms ease,
    transform 300ms cubic-bezier(0.21, 1.02, 0.73, 1);
}

.tw-toast-leave-active {
  transition:
    opacity 200ms ease,
    transform 200ms ease;
}

.tw-toast-enter {
  opacity: 0;
  transform: translateY(100%);
}

.tw-toast-leave-to {
  opacity: 0;
  transform: translateY(100%);
}
</style>
